import React from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { ChevronLeft, Calendar } from "lucide-react";
import BLOG_ARTICLES from "../data/blogArticles";

export default function BlogArticle() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/\/$/, "");
  const article = BLOG_ARTICLES.find((p) => p.url.replace(/\/$/, "") === slug);

  if (!article || !article.body) return <Navigate to="/blog" replace />;

  return (
    <div>
      <section className="bg-[#f4f5f7] py-14 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="text-3xl md:text-4xl font-semibold text-slate-800 uppercase tracking-wide leading-tight">
            {article.title}
          </h1>
          {article.date && (
            <div className="mt-4 flex items-center gap-1.5 text-sm text-slate-500">
              <Calendar className="w-4 h-4" />
              <span>{article.date}</span>
            </div>
          )}
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1 text-sm text-[#29abe2] hover:underline mb-10 block"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <div className="space-y-5 text-slate-700 leading-relaxed text-[16px]">
          {article.body.map((item, i) => {
            if (typeof item === "string") {
              return <p key={i}>{item}</p>;
            }
            if (item.heading) {
              return (
                <h2 key={i} className="text-2xl font-semibold text-slate-800 pt-4">
                  {item.heading}
                </h2>
              );
            }
            if (item.list) {
              return (
                <ul key={i} className="list-disc pl-6 space-y-2">
                  {item.list.map((li, j) => (
                    <li key={j}>{li}</li>
                  ))}
                </ul>
              );
            }
            if (item.code) {
              return (
                <pre key={i} className="bg-slate-100 rounded p-4 text-sm overflow-x-auto text-slate-800 font-mono">
                  <code>{item.code}</code>
                </pre>
              );
            }
            if (item.image) {
              return (
                <figure key={i} className="my-6">
                  <img
                    src={item.image}
                    alt={item.alt || ""}
                    className={item.imagecss || "w-auto block m-auto"}
                  />
                  {item.alt && (
                    <figcaption className="text-center text-sm text-slate-500 mt-2 italic">
                      {item.alt}
                    </figcaption>
                  )}
                </figure>
              );
            }
            if (item.blockquote) {
              return (
                <blockquote key={i} className="my-6 mx-6 border-l-4 border-[#29abe2] pl-6 py-2">
                  <p className="italic text-slate-600 leading-loose text-[16px]">
                    {item.blockquote}
                  </p>
                  {item.cite && (
                    <cite className="block mt-3 text-sm font-semibold not-italic text-slate-500">
                      {item.cite}
                    </cite>
                  )}
                </blockquote>
              );
            }
            if (item.html) {
              return (
                <p
                  key={i}
                  className="blog-html"
                  dangerouslySetInnerHTML={{ __html: item.html }}
                />
              );
            }
            if (item.links) {
              return (
                <div key={i} className="flex flex-wrap gap-3 items-center">
                  {item.links.map((l, j) => (
                    <a
                      key={j}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={l.label || l.href}
                    >
                      <img src={l.icon} alt={l.label || ""} className="h-10 w-auto" />
                    </a>
                  ))}
                </div>
              );
            }
            return null;
          })}
        </div>
      </article>
    </div>
  );
}
